import React, { VFC, useEffect, useMemo } from 'react';

import { NorthRounded } from '@mui/icons-material';
import { useGetPaletteColors } from 'return-portal-ui';

import { DisplayStep, OutvioStatusCodes, StepStatus } from '../../../../Interfaces';
import Dot from './Icons/Dot';

interface IconContainerProps {
  step: DisplayStep;
}

type LineType = StepStatus | 'none';
type IconType = 'dot' | 'none';

const GRAY_LINE_STATUS = [StepStatus.BACKTRACK, StepStatus.BACKTRACK_INCIDENT, StepStatus.PENDING];
const BACKTRACK_STATUS = [StepStatus.BACKTRACK, StepStatus.BACKTRACK_INCIDENT];

const getLine = ({
  lineStatus,
  stepStatus,
}: {
  lineStatus?: StepStatus;
  stepStatus: StepStatus;
}): LineType => {
  if (!lineStatus) return 'none';
  return !GRAY_LINE_STATUS.includes(stepStatus) && !GRAY_LINE_STATUS.includes(lineStatus)
    ? StepStatus.DONE
    : StepStatus.PENDING;
};

const useIconContainer = ({
  step: { status, prevStatus, nextStatus, isSubStep, parent },
}: IconContainerProps): { topLine: LineType; bottomLine: LineType; icon: IconType } => {
  if (isSubStep && parent)
    return {
      icon: 'none',
      bottomLine: parent.nextStatus || 'none',
      topLine: parent.nextStatus || 'none',
    };
  const topLine = getLine({ lineStatus: prevStatus, stepStatus: status });
  const bottomLine = getLine({ lineStatus: nextStatus, stepStatus: status });
  return { icon: 'dot', topLine, bottomLine };
};

const BacktrackArrow: VFC<{ step: DisplayStep; pos: 'top' | 'bot' }> = ({
  step: { status, isSubStep },
  pos,
}) => {
  const { secondaryBg, primaryText } = useGetPaletteColors();

  if (BACKTRACK_STATUS.includes(status) && !isSubStep && pos === 'top') {
    return (
      <div className="absolute z-[10] left-0 right-0 mx-auto w-min bottom-[36px]">
        <NorthRounded
          style={{ stroke: secondaryBg, color: primaryText }}
          className="stroke-[4px]"
          sx={{ paintOrder: 'stroke', fontSize: '16px' }}
        />
      </div>
    );
  }

  return null;
};

const Line: VFC<{ line: LineType; pos: 'bot' | 'top'; step: DisplayStep }> = ({
  line,
  pos,
  step,
}) => {
  const { btnBg } = useGetPaletteColors();

  // Return line null if the line is not needed
  if (line === 'none') return null;

  if (
    pos === 'top' &&
    step.code === OutvioStatusCodes.DELIVERED_TO_SHOP &&
    step.prevStatus === 'done'
  ) {
    return (
      <div
        style={{ backgroundColor: btnBg }}
        className="z-[8] h-full absolute left-0 right-0 mx-auto w-[2px] top-0"
      />
    );
  }

  if (
    pos === 'bot' &&
    step.code === OutvioStatusCodes.DELIVERED_TO_SHOP &&
    step.nextStatus === 'done'
  ) {
    return (
      <div
        style={{ backgroundColor: btnBg }}
        className="z-[7] h-full absolute left-0 right-0 mx-auto w-[2px] bottom-0"
      />
    );
  }

  if (pos === 'bot' && step.code === OutvioStatusCodes.DELIVERED_TO_SHOP) {
    return (
      <div
        style={{ backgroundColor: btnBg }}
        className="z-[7] h-full absolute left-0 right-0 mx-auto w-[1px] bottom-0 opacity-20"
      />
    );
  }

  return (
    <>
      <BacktrackArrow step={step} pos={pos} />
      <div
        style={{ backgroundColor: btnBg }}
        className={`z-[8] h-1/2 absolute left-0 right-0 mx-auto ${
          GRAY_LINE_STATUS.includes(line) ? 'w-[1px] opacity-20' : 'w-[2px]'
        } ${pos === 'bot' ? 'bottom-0' : 'top-0'}`}
      />
    </>
  );
};

const Icon: VFC<{ step: DisplayStep; icon: IconType }> = ({ step, icon }) => {
  switch (icon) {
    case 'dot':
      return <Dot step={step} />;
    case 'none':
    default:
      return null;
  }
};

const IconContainer: VFC<IconContainerProps> = ({ step }) => {
  const { topLine, bottomLine, icon } = useIconContainer({ step });

  const { secondaryBg, btnBg } = useGetPaletteColors();

  const ref = React.createRef<HTMLDivElement>();

  const isLastDoneStepClassNames = useMemo<string>(() => {
    if (!step.isLastDoneStep) return '';
    if (step.code === OutvioStatusCodes.DELIVERED_TO_SHOP) {
      return 'm-auto top-[13px] bottom-auto right-0 left-0 w-[20px] h-[20px] absolute z-[10] rounded-full flex items-center justify-center';
    }
    return 'm-auto top-0 bottom-0 right-0 left-0 w-[20px] h-[20px] absolute z-[10] rounded-full flex items-center justify-center';
  }, [step.code, step.isLastDoneStep]);

  useEffect(() => {
    if (ref.current) {
      const targetElement = ref.current;

      targetElement.style.scrollSnapAlign = 'center';
      const timer = setTimeout(() => {
        targetElement.style.scrollSnapAlign = 'none';
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [ref, step]);

  return (
    <div className="relative flex">
      {step.isLastDoneStep && (
        <div
          ref={ref}
          style={{ backgroundColor: secondaryBg }}
          className={isLastDoneStepClassNames}
        >
          <div
            style={{ backgroundColor: btnBg }}
            className="relative w-[16px] h-[16px] rounded-full opacity-20"
          >
            <div
              style={{ backgroundColor: btnBg }}
              className="animate-ping absolute w-[16px] h-[16px] rounded-full"
            />
          </div>
        </div>
      )}
      <Line line={topLine} pos="top" step={step} />
      <Icon icon={icon} step={step} />
      <Line line={bottomLine} pos="bot" step={step} />
    </div>
  );
};

export default IconContainer;
